/**
 * Normalización única de estados remotos de servicios (rides y reserves).
 *
 * Port bit a bit de `rider_app/lib/core/utils/service_status_mapper.dart`.
 * Functions mantiene `server_phase` como autoridad y `status` como
 * compatibilidad temporal. La app usa estos estados locales canónicos para no
 * depender de alias legacy como `on trip`, `ended` u `onride`.
 *
 * CONTRATO CROSS-APP (inmutable): las 5 superficies (admin web, rider cliente,
 * rider guest, driver móvil + 2 móviles congeladas) DEBEN tolerar en lectura
 * los mismos valores legacy. No alterar este mapeo sin actualizar
 * `rider_app/docs/qa/cross_app_contract.md` en los repos afectados.
 */

/** Estados canónicos de un ride/viaje. */
export const RIDE_STATUSES = [
  'pending',
  'accepted',
  'arrived',
  'in_progress',
  'completed',
  'cancelled',
  'no_show',
] as const;
export type RideStatus = (typeof RIDE_STATUSES)[number];

/** Estados canónicos de una reserva. */
export const RESERVE_STATUSES = [
  'pending',
  'accepted',
  'confirmed',
  'in_progress',
  'completed',
  'cancelled',
  'no_show',
] as const;
export type ReserveStatus = (typeof RESERVE_STATUSES)[number];

/**
 * Normaliza un estado de ride al conjunto canónico, tolerando alias legacy.
 *
 * Devuelve `null` para null/vacío (paridad con el `null` de Dart). Un valor
 * desconocido se devuelve **trimmed + lowercased** (no el original), igual que
 * el `default: return status;` de Dart.
 */
export function normalizeRideStatus(value: string | null | undefined): string | null {
  const status = value?.trim().toLowerCase();
  switch (status) {
    case undefined:
    case '':
      return null;
    case 'requested':
    case 'offered':
    case 'pending':
    case 'waiting':
    case 'esperando aceptacion':
    case 'esperando aceptacion...':
    case 'esperando aceptación':
    case 'esperando aceptación...':
      return 'pending';
    case 'accepted':
    case 'arriving':
    case 'aceptado':
      return 'accepted';
    case 'arrived':
      return 'arrived';
    case 'in_progress':
    case 'ontrip':
    case 'onride':
    case 'on ride':
    case 'on trip':
    case 'on_trip':
    case 'en ruta':
      return 'in_progress';
    case 'completed':
    case 'ended':
    case 'done':
    case 'finalizado':
      return 'completed';
    case 'cancelled':
    case 'canceled':
    case 'cancelado':
      return 'cancelled';
    case 'no_show':
    case 'noshow':
    case 'no show':
      return 'no_show';
    default:
      return status;
  }
}

/**
 * Normaliza un estado de reserva al conjunto canónico, tolerando alias legacy.
 * Mismas reglas de null/vacío/desconocido que {@link normalizeRideStatus}.
 */
export function normalizeReserveStatus(value: string | null | undefined): string | null {
  const status = value?.trim().toLowerCase();
  switch (status) {
    case undefined:
    case '':
      return null;
    case 'reserved':
    case 'dispatch_pending':
    case 'offered':
    case 'pending':
    case 'waiting':
    case 'requested':
    case 'esperando aceptacion':
    case 'esperando aceptacion...':
    case 'esperando aceptación':
    case 'esperando aceptación...':
      return 'pending';
    case 'accepted':
    case 'assigned':
    case 'aceptado':
      return 'accepted';
    case 'confirmed':
      return 'confirmed';
    case 'arrived':
    case 'in_progress':
    case 'ontrip':
    case 'onride':
    case 'on ride':
    case 'on trip':
    case 'on_trip':
    case 'en ruta':
      return 'in_progress';
    case 'completed':
    case 'ended':
    case 'done':
    case 'finalizado':
      return 'completed';
    case 'cancelled':
    case 'canceled':
    case 'cancelado':
      return 'cancelled';
    case 'no_show':
    case 'noshow':
    case 'no show':
      return 'no_show';
    default:
      return status;
  }
}

/**
 * Fase de ride que se ESCRIBE en RTDB para un estado dado (autoridad
 * `server_phase`). Port de `ServiceStatusMapper.ridePhaseForStatus`.
 */
export function ridePhaseForStatus(value: string): string {
  switch (normalizeRideStatus(value)) {
    case 'pending':
      return 'requested';
    case 'accepted':
      return 'accepted';
    case 'arrived':
      return 'arrived';
    case 'in_progress':
      return 'in_progress';
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'no_show':
      return 'no_show';
    default:
      return value.trim().toLowerCase();
  }
}

/**
 * Fase de reserva que se ESCRIBE en RTDB para un estado dado.
 * Port de `ServiceStatusMapper.reservePhaseForStatus`.
 */
export function reservePhaseForStatus(value: string): string {
  switch (normalizeReserveStatus(value)) {
    case 'pending':
      return 'reserved';
    case 'accepted':
      return 'accepted';
    case 'confirmed':
      return 'confirmed';
    case 'in_progress':
      return 'in_progress';
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'no_show':
      return 'no_show';
    default:
      return value.trim().toLowerCase();
  }
}
